import { useCallback, useEffect, useState } from "react";
import type { AdminFapsCodeBatch, AdminFapsLedgerRow, AdminUserSummary, FapsKind } from "@gooner/shared";
import { formatFaps } from "@gooner/shared";
import { adminApi } from "../lib/api";
import { Button, TextInput } from "../control/ui/primitives";
import { CrownMark } from "../control/ui/Crown";
import { FapsAmount } from "../control/ui/FapsAmount";

/**
 * Faps, from the operator's side: mint the redeem links a Patreon shop purchase is paid
 * out through, see how each batch is claimed, adjust one account with a note, and read
 * the ledger. The rates and the switch live in System → Faps.
 *
 * THE FLOW IS BY HAND (2026-09-08). Patreon's API reports memberships, never shop
 * purchases, so a purchase is fulfilled by a person: the buyer pays on Patreon → the
 * operator mints a code here → messages the buyer its LINK on Patreon → the buyer opens
 * it and claims for their account (/faps/redeem?code=… opens the wallet on the redeem
 * form, prefilled — one press). So the mint's reply is presented as links, one per code,
 * each with its own Copy: the operator's unit of work is one message to one buyer.
 *
 * THE CODES ARE SHOWN ONCE. The server keeps hashes; the mint's reply is the only time
 * the plaintext exists, so this panel puts them in a box with Copy and Download and says
 * so — closing the panel is losing them.
 */
export function FapsAdmin() {
  return (
    <div className="space-y-10">
      <MintCodes />
      <GrantForm />
      <Ledger />
    </div>
  );
}

function MintCodes() {
  const [batches, setBatches] = useState<AdminFapsCodeBatch[]>([]);
  const [amount, setAmount] = useState("500");
  // One purchase, one link: the by-hand flow mints per buyer, so the count starts at 1.
  const [count, setCount] = useState("1");
  const [label, setLabel] = useState("Patreon shop — 500 pack");
  const [copied, setCopied] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [minted, setMinted] = useState<{ batchId: string; codes: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { batches: list } = await adminApi.fapsListBatches();
    setBatches(list);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const mint = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await adminApi.fapsMintCodes(Number(count), Number(amount), label.trim());
      setMinted(r);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message.replace(/^\d+:\s*/, "") : "Couldn't mint.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (batchId: string) => {
    if (!window.confirm("Cancel every unredeemed code in this batch? Redeemed ones stay redeemed.")) return;
    await adminApi.fapsRevokeBatch(batchId);
    await load();
  };

  // The link the buyer gets. Same origin as this panel: the admin is the site.
  const linkFor = (code: string) => `${window.location.origin}/faps/redeem?code=${encodeURIComponent(code)}`;
  const links = minted?.codes.map(linkFor) ?? [];
  const text = links.join("\n");
  const copy = (s: string, key: string) => {
    void navigator.clipboard.writeText(s);
    setCopied(key);
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="flex items-center gap-2 text-lg font-semibold text-zinc-100">
          <CrownMark size={18} /> Redeem codes
        </h1>
        <p className="text-xs text-zinc-500">
          Someone buys a pack on Patreon → mint a code here → message them its link on Patreon →
          they open it and claim for their account, one press. Each link is one-time and worth
          its batch&rsquo;s amount; the batches below show what has been claimed.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-4 sm:grid-cols-4">
        <label className="block">
          <span className="mb-1 block text-xs text-zinc-400">Faps per code</span>
          <TextInput value={amount} onChange={(e) => setAmount(e.target.value)} className="text-center" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-zinc-400">How many (≤ 500)</span>
          <TextInput value={count} onChange={(e) => setCount(e.target.value)} className="text-center" />
        </label>
        <label className="block sm:col-span-2">
          <span className="mb-1 block text-xs text-zinc-400">Label (what the buyer sees in their ledger)</span>
          <TextInput value={label} onChange={(e) => setLabel(e.target.value)} />
        </label>
        <div className="sm:col-span-4">
          <Button onClick={() => void mint()} disabled={busy || !(Number(amount) > 0) || !(Number(count) > 0)}>
            {busy ? "Minting…" : "Mint codes"}
          </Button>
          {error ? <span className="ml-3 text-xs text-red-400">{error}</span> : null}
        </div>
      </div>

      {minted ? (
        <div className="rounded-xl border border-[#f5c518]/40 bg-[#f5c518]/5 p-4">
          <p className="mb-2 text-sm font-medium text-zinc-100">
            {minted.codes.length === 1 ? "1 link" : `${minted.codes.length} links`} minted —{" "}
            <span className="text-amber-300">shown once. Copy {minted.codes.length === 1 ? "it" : "them"} now.</span>
          </p>
          {/* One row per link with its own Copy — the unit of work is one message to one
              buyer. Past a handful the list is a batch, and the box + Copy all is the tool. */}
          {links.length <= 10 ? (
            <ul className="space-y-1.5">
              {links.map((l, i) => (
                <li key={minted.codes[i]} className="flex items-center gap-2">
                  <code className="min-w-0 flex-1 truncate rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel-2)] px-2 py-1.5 font-mono text-xs text-zinc-100">
                    {l}
                  </code>
                  <Button size="sm" onClick={() => copy(l, minted.codes[i])}>
                    {copied === minted.codes[i] ? "Copied" : "Copy link"}
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <textarea
              readOnly
              value={text}
              rows={12}
              className="w-full rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel-2)] p-2 font-mono text-xs text-zinc-100"
            />
          )}
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={() => copy(text, "all")}>
              {copied === "all" ? "Copied" : links.length === 1 ? "Copy link" : "Copy all links"}
            </Button>
            <a
              className="rounded-md border border-white/15 px-2.5 py-1.5 text-xs text-zinc-200 hover:bg-white/10"
              href={`data:text/plain;charset=utf-8,${encodeURIComponent(text)}`}
              download={`faps-links-${minted.batchId.slice(0, 8)}.txt`}
            >
              Download .txt
            </a>
            <Button size="sm" variant="ghost" onClick={() => { setMinted(null); setCopied(null); }}>
              Done — I&rsquo;ve {minted.codes.length === 1 ? "sent it" : "saved them"}
            </Button>
          </div>
        </div>
      ) : null}

      {batches.length === 0 ? (
        <p className="text-sm text-zinc-500">No batches yet.</p>
      ) : (
        <ul className="space-y-2">
          {batches.map((b) => (
            <li
              key={b.batchId}
              className="flex items-center gap-3 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-2.5"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-zinc-100">
                  {b.label ?? "(no label)"}
                  <span className="ml-2 font-normal text-zinc-500">
                    <FapsAmount n={b.amount} size={10} gold={false} /> each
                  </span>
                </div>
                <div className="text-xs text-zinc-500">
                  {b.redeemed} / {b.count} redeemed
                  {b.revoked ? ` · ${b.revoked} cancelled` : ""} · minted {new Date(b.createdAt).toLocaleDateString()}
                </div>
              </div>
              {b.redeemed + b.revoked < b.count ? (
                <Button size="sm" variant="ghost" onClick={() => void revoke(b.batchId)}>
                  Cancel unredeemed
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function GrantForm() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [picked, setPicked] = useState<AdminUserSummary | null>(null);
  const [delta, setDelta] = useState("100");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setUsers([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      adminApi
        .listUsers(q.trim())
        .then((r) => !cancelled && setUsers(r.users.slice(0, 8)))
        .catch(() => undefined);
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  const grant = async () => {
    if (!picked) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await adminApi.fapsGrant(picked.id, Number(delta), note.trim());
      setResult(`@${picked.username} now holds ${formatFaps(r.balance)}.`);
    } catch (e) {
      setResult(e instanceof Error ? e.message.replace(/^\d+:\s*/, "") : "Couldn't adjust.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <header>
        <h2 className="text-base font-semibold text-zinc-100">Adjust an account</h2>
        <p className="text-xs text-zinc-500">
          A positive number is a gift, a negative one a clawback (never below zero). The note is
          required — the ledger is the audit trail, and the person sees it too.
        </p>
      </header>
      <div className="grid grid-cols-1 gap-3 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-4 sm:grid-cols-4">
        <label className="relative block sm:col-span-2">
          <span className="mb-1 block text-xs text-zinc-400">Account</span>
          <TextInput
            value={picked ? `@${picked.username}` : q}
            onChange={(e) => {
              setPicked(null);
              setQ(e.target.value);
            }}
            placeholder="username"
          />
          {!picked && users.length > 0 ? (
            <ul className="absolute z-10 mt-1 w-full rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel-2)] py-1 shadow-xl">
              {users.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-1.5 text-left text-sm text-zinc-200 hover:bg-white/10"
                    onClick={() => {
                      setPicked(u);
                      setUsers([]);
                    }}
                  >
                    @{u.username}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-zinc-400">Faps (±)</span>
          <TextInput value={delta} onChange={(e) => setDelta(e.target.value)} className="text-center" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-zinc-400">Note</span>
          <TextInput value={note} onChange={(e) => setNote(e.target.value)} placeholder="why" />
        </label>
        <div className="sm:col-span-4">
          <Button onClick={() => void grant()} disabled={busy || !picked || !note.trim() || !Number.isInteger(Number(delta)) || Number(delta) === 0}>
            {busy ? "Saving…" : "Apply"}
          </Button>
          {result ? <span className="ml-3 text-xs text-zinc-300">{result}</span> : null}
        </div>
      </div>
    </div>
  );
}

const KINDS: ReadonlyArray<FapsKind | ""> = ["", "welcome", "fap", "watch", "chest", "quest", "stipend", "code", "wall", "admin"];

function Ledger() {
  const [rows, setRows] = useState<AdminFapsLedgerRow[]>([]);
  const [kind, setKind] = useState<FapsKind | "">("");
  const [mismatches, setMismatches] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .fapsLedger(200, kind || undefined)
      .then((r) => !cancelled && setRows(r.rows))
      .catch(() => undefined);
    adminApi
      .fapsAudit()
      .then((r) => !cancelled && setMismatches(r.mismatches.length))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [kind]);

  return (
    <div className="space-y-3">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-zinc-100">Ledger</h2>
          <p className="text-xs text-zinc-500">
            Every move, newest first.
            {mismatches === null ? "" : mismatches === 0 ? " Every balance matches its ledger." : ` ${mismatches} balance(s) DISAGREE with their ledger — look.`}
          </p>
        </div>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as FapsKind | "")}
          className="rounded-md border border-[var(--color-edge)] bg-[var(--color-panel-2)] px-2 py-1 text-xs text-zinc-200"
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {k || "all kinds"}
            </option>
          ))}
        </select>
      </header>
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500">Nothing yet.</p>
      ) : (
        <ul className="divide-y divide-[var(--color-edge)] rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)]">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center gap-3 px-3 py-1.5 text-xs">
              <span className="w-32 shrink-0 text-zinc-500">{new Date(r.createdAt).toLocaleString()}</span>
              <span className="w-28 shrink-0 truncate text-zinc-200">@{r.username}</span>
              <span className="w-16 shrink-0 text-zinc-500">{r.kind}</span>
              <span className="min-w-0 flex-1 truncate text-zinc-500">{r.note ?? r.ref ?? ""}</span>
              <FapsAmount n={r.delta} signed size={10} gold={r.delta >= 0} className={r.delta < 0 ? "text-zinc-400" : ""} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
